import { TOKEN_PROGRAM_ID, AccountLayout, u64, MintLayout, Token, ASSOCIATED_TOKEN_PROGRAM_ID, NATIVE_MINT } from '@solana/spl-token';
import { bs58 } from '@coral-xyz/anchor/dist/cjs/utils/bytes';
import { TransactionExpiredBlockheightExceededError, VersionedTransaction, PublicKey, SystemProgram, Transaction, ComputeBudgetProgram, TransactionInstruction, ComputeBudgetInstruction, PACKET_DATA_SIZE } from '@solana/web3.js';
import promiseRetry from 'promise-retry';
import { utils } from '@coral-xyz/anchor';
import { getAssociatedTokenAddress, createAssociatedTokenAccountIdempotentInstruction, unpackMint } from '@solana/spl-token-0.4';
import { Metadata } from '@metaplex-foundation/mpl-token-metadata';

class TransactionError extends Error {
  constructor(m, txid, code, programIds, serializedTx) {
    super(m);
    this.txid = void 0;
    this.code = void 0;
    this.programIds = void 0;
    this.serializedTx = void 0;
    this.txid = txid;
    this.code = code;
    this.programIds = programIds;
    this.serializedTx = serializedTx;
    // Set the prototype explicitly.
    Object.setPrototypeOf(this, Error.prototype);
  }
}
const COMMON_TX_ERRORS = {
  PROGRAM_FAILED_TO_COMPLETE_ERROR: 'Program failed to complete',
  UNKNOWN_ERROR: 'Unknown error, visit the explorer',
  TOO_MANY_ACCOUNT_LOCKS: 'Transaction locked too many accounts',
  INSUFFICIENT_FUNDS_FOR_RENT: 'The account does not have enough SOL to perform the operation',
  TRANSACTION_TOO_LARGE: 'Transaction too large',
  TRANSACTION_NOT_CONFIRMED: 'Transaction has expired and was not confirmed. Please try again.',
  FROZEN_ACCOUNT: 'The token account is frozen',
  TRANSACTION_EXPIRED_BLOCKHEIGHT_EXCEEDED: 'TransactionExpiredBlockheightExceededError'
};
const SYSTEM_PROGRAM_ID = '11111111111111111111111111111111';
function parseErrorForTransaction({
  err,
  logMessages
}) {
  const transactionError = err;
  let errorCode;
  if (transactionError && typeof transactionError !== 'string') {
    let error = transactionError;
    if ('InsufficientFundsForRent' in error) {
      return {
        message: COMMON_TX_ERRORS.INSUFFICIENT_FUNDS_FOR_RENT,
        code: 1,
        programIds: [TOKEN_PROGRAM_ID.toBase58()]
      };
    } else {
      const instructionError = transactionError.InstructionError;
      const [_, errorMsg] = instructionError;
      if (typeof errorMsg === 'string') {
        if (errorMsg === 'ProgramFailedToComplete') {
          if (logMessages) {
            const failedProgramIds = getFailedExceededInstructionsPrograms(logMessages);
            if (failedProgramIds.length > 0) {
              return {
                message: COMMON_TX_ERRORS.PROGRAM_FAILED_TO_COMPLETE_ERROR,
                programIds: failedProgramIds,
                code: errorCode
              };
            }
          }
        }
      } else {
        errorCode = errorMsg.Custom ?? instructionError[1];
        if (logMessages) {
          const failedProgramId = getFailedProgram(logMessages, errorCode);
          if (failedProgramId) {
            if (failedProgramId === SYSTEM_PROGRAM_ID) {
              return getSystemProgramError(instructionError);
            }
            if (failedProgramId === TOKEN_PROGRAM_ID.toBase58()) {
              switch (errorCode) {
                case 17:
                  {
                    return {
                      message: COMMON_TX_ERRORS.FROZEN_ACCOUNT,
                      programIds: [failedProgramId],
                      code: errorCode
                    };
                  }
              }
            }
            return {
              message: COMMON_TX_ERRORS.UNKNOWN_ERROR,
              programIds: [failedProgramId],
              code: errorCode
            };
          }
        }
      }
    }
  }
  if (transactionError === 'InsufficientFundsForFee') {
    return {
      message: COMMON_TX_ERRORS.INSUFFICIENT_FUNDS_FOR_RENT,
      code: 1,
      programIds: [TOKEN_PROGRAM_ID.toBase58()]
    };
  } else if (transactionError === 'TooManyAccountLocks') {
    return {
      message: COMMON_TX_ERRORS.TOO_MANY_ACCOUNT_LOCKS,
      code: 0,
      programIds: []
    };
  }
  return {
    message: typeof transactionError === 'string' ? transactionError || COMMON_TX_ERRORS.UNKNOWN_ERROR : COMMON_TX_ERRORS.UNKNOWN_ERROR,
    code: errorCode
  };
}
function getFailedExceededInstructionsPrograms(logMessages) {
  let founds = [];
  for (let i = 0; i < logMessages.length; i++) {
    const log = logMessages[i];
    const found = log.match(new RegExp(/Program (\S+) failed: (?:Program failed to complete|SBF program panicked)/));
    if (found) {
      founds.push(found[1]);
    }
  }
  return founds;
}
function getFailedProgram(logMessages, errorCode) {
  for (let i = 0; i < logMessages.length; i++) {
    const log = logMessages[i];
    const found = log.match(new RegExp(`Program ([1-9A-HJ-NP-Za-km-z]{32,44}) failed: custom program error: 0x${errorCode.toString(16)}`));
    if (found) {
      return found[1];
    }
  }
  return;
}
function getSystemProgramError(instructionError) {
  const code = instructionError[1].Custom;
  let message = '';
  switch (code) {
    // https://github.com/solana-labs/solana/blob/22a18a68e3ee68ae013d647e62e12128433d7230/sdk/program/src/system_instruction.rs#L12-L26
    // TODO: Do we need to translate all error codes.
    case 0:
      {
        message = 'An account with the same address already exists';
        break;
      }
    case 1:
      {
        message = COMMON_TX_ERRORS.INSUFFICIENT_FUNDS_FOR_RENT;
        break;
      }
    default:
      {
        message = COMMON_TX_ERRORS.UNKNOWN_ERROR;
      }
  }
  return {
    code,
    programIds: [SYSTEM_PROGRAM_ID],
    message
  };
}

const wait = time => new Promise(resolve => setTimeout(resolve, time));

const SEND_OPTIONS = {
  maxRetries: 0,
  skipPreflight: true
};
async function forwardToJito(serializedTransaction, jitoTipTx, jitoProxyUrl) {
  try {
    const jitoResponse = await fetch(jitoProxyUrl ?? `https://mainnet.block-engine.jito.wtf/api/v1/bundles`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        jsonrpc: '2.0',
        id: 1,
        method: 'sendBundle',
        params: [[bs58.encode(serializedTransaction), bs58.encode(jitoTipTx.serialize())]]
      })
    });
    const jitoResult = await jitoResponse.json();
    if (jitoResult.result) {
      console.log('Sent to JITO', jitoResult.result);
      return jitoResult;
    }
  } catch (error) {
    console.log('Failed to send to Jito', error);
  }
  return null;
}
async function transactionSenderAndConfirmationWaiter({
  txid,
  connection,
  serializedTransaction,
  blockhashWithBlockHeight,
  broadcastMode,
  jitoTipTx,
  jitoProxyUrl
}) {
  const controller = new AbortController();
  const abortSignal = controller.signal;
  const abortableResender = async () => {
    while (true) {
      await wait(1_750);
      if (abortSignal.aborted) return;
      try {
        await connection.sendRawTransaction(serializedTransaction, SEND_OPTIONS);
      } catch (e) {
        console.warn(`Failed to resend transaction: ${e}`);
      }
    }
  };
  let jitoResponse = null;
  try {
    if (broadcastMode === 'jito' && jitoTipTx) {
      jitoResponse = await forwardToJito(serializedTransaction, jitoTipTx, jitoProxyUrl);
    } else {
      if (broadcastMode === 'mixed' && jitoTipTx) {
        jitoResponse = await forwardToJito(serializedTransaction, jitoTipTx, jitoProxyUrl);
      }
      await connection.sendRawTransaction(serializedTransaction, SEND_OPTIONS), abortableResender();
    }
    // this would throw TransactionExpiredBlockheightExceededError
    await Promise.race([connection.confirmTransaction({
      ...blockhashWithBlockHeight,
      signature: txid,
      abortSignal
    }, 'confirmed'), new Promise(async resolve => {
      // in case ws socket died
      while (!abortSignal.aborted) {
        await wait(2_000);
        const tx = await connection.getSignatureStatus(txid, {
          searchTransactionHistory: false
        });
        if (tx?.value?.confirmationStatus === 'confirmed') {
          resolve(tx);
        }
      }
    })]);
  } catch (e) {
    if (e instanceof TransactionExpiredBlockheightExceededError) {
      // we consume this error and getTransaction would return null
      return {
        response: null,
        jitoResponse
      };
    } else {
      // invalid state from web3.js
      throw e;
    }
  } finally {
    controller.abort();
  }
  // in case rpc is not synced yet, we add some retries
  const response = await promiseRetry(async retry => {
    const response = await connection.getTransaction(txid, {
      commitment: 'confirmed',
      maxSupportedTransactionVersion: 0
    });
    if (!response) {
      retry(response);
    }
    return response;
  }, {
    retries: 5,
    minTimeout: 1e3
  });
  return {
    response,
    jitoResponse
  };
}

function getSignature(transaction) {
  const signature = 'signature' in transaction ? transaction.signature : transaction.signatures[0];
  if (!signature) {
    throw new Error('Missing transaction signature, the transaction was not signed by the fee payer');
  }
  return utils.bytes.bs58.encode(signature);
}
const handleSendTransaction = async ({
  connection,
  signedTransaction,
  blockhash,
  lastValidBlockHeight,
  idl,
  skipPreflight,
  idlProgramId,
  minContextSlot,
  broadcastMode,
  jitoTipTx,
  jitoProxyUrl,
  debug
}) => {
  let serializedTransaction = Buffer.from(signedTransaction.serialize());
  let signature = getSignature(signedTransaction);
  const {
    value: simulatedTransactionResponse
  } = await connection.simulateTransaction('message' in signedTransaction ? signedTransaction : new VersionedTransaction(signedTransaction.compileMessage()), {
    replaceRecentBlockhash: true,
    commitment: 'processed',
    minContextSlot
  });
  const {
    err,
    logs
  } = simulatedTransactionResponse;
  if (debug) {
    console.log('Simulated Transaction Logs:', logs);
  }
  if (err) {
    // dont save txid to error cause it is not sent
    const error = parseErrorWithIdl({
      err,
      logMessages: logs
    }, undefined, idl, idlProgramId);
    if (skipPreflight) {
      if (error.message === COMMON_TX_ERRORS.INSUFFICIENT_FUNDS_FOR_RENT || error.message === COMMON_TX_ERRORS.TOO_MANY_ACCOUNT_LOCKS) {
        error.serializedTx = Buffer.from(signedTransaction.serialize()).toString('base64');
        return {
          error,
          txid: undefined
        };
      }
    } else {
      error.serializedTx = Buffer.from(signedTransaction.serialize()).toString('base64');
      return {
        error,
        txid: undefined
      };
    }
  }
  const {
    response: transactionResponse,
    jitoResponse
  } = await transactionSenderAndConfirmationWaiter({
    txid: signature,
    connection,
    serializedTransaction,
    blockhashWithBlockHeight: {
      blockhash,
      lastValidBlockHeight
    },
    broadcastMode,
    jitoTipTx,
    jitoProxyUrl
  });
  if (!transactionResponse) {
    const error = new TransactionError(COMMON_TX_ERRORS.TRANSACTION_NOT_CONFIRMED);
    error.serializedTx = Buffer.from(signedTransaction.serialize()).toString('base64');
    return {
      error,
      txid: undefined
    };
  }
  if (transactionResponse.meta?.err) {
    const error = parseErrorWithIdl(transactionResponse.meta, signature, idl, idlProgramId);
    error.serializedTx = Buffer.from(signedTransaction.serialize()).toString('base64');
    return {
      error,
      transactionResponse,
      txid: signature
    };
  }
  return {
    transactionResponse,
    jitoResponse,
    txid: signature
  };
};
const parseErrorWithIdl = (errorParams, txid, idl, idlProgramId) => {
  const {
    code,
    programIds,
    message
  } = parseErrorForTransaction(errorParams);
  if (idlProgramId && idl) {
    if (programIds?.includes(idlProgramId.toBase58())) {
      let error = idl.errors?.find(error => {
        return error.code === code;
      });
      if (error) {
        // msg might not be exposed by IDL, so we fallback to name
        return new TransactionError(error.msg ?? error.name, txid, error.code, programIds);
      }
    }
  }
  return new TransactionError(message, txid, code, programIds);
};
const JITO_TIP_ACCOUNTS = ['96gYZGLnJYVFmbjzopPSU6QiEV5fGqZNyN9nmNhvrZU5', 'HFqU5x63VTqvQss8hp11i4wVV8bD44PvwucfZ2bU7gRe', 'Cw8CFyM9FkoMi7K7Crf6HNQqf4uEMzpKw6QNghXLvLkY', 'ADaUMid9yfUytqMBgopwjb2DTLSokTSzL1zt6iGPaS49', 'DfXygSm4jCyNCybVYYK6DwvWqjKee8pbDmJGcLWNDXjh', 'ADuUkR4vqLUMWXxW9gh6D6L8pMSawimctcNZ5pGwDcEt', 'DttWaMuVvTiduZRnguLF7jNxTgiMBZ1hyAumKUiL2KRL', '3AVi9Tg9Uo68tJfuvoKvqKNWKkC5wPdSSdeBnizKZ6jT'];
const getRandomTipAccount = () => new PublicKey(JITO_TIP_ACCOUNTS[Math.floor(Math.random() * JITO_TIP_ACCOUNTS.length)]);
function createJitoTipTx({
  payer,
  lamports,
  blockhash,
  lastValidBlockHeight
}) {
  const tipAccount = getRandomTipAccount();
  const tipIx = SystemProgram.transfer({
    fromPubkey: payer,
    toPubkey: tipAccount,
    lamports: BigInt(lamports)
  });
  const tx = new Transaction({
    blockhash,
    lastValidBlockHeight
  }).add(tipIx);
  tx.add(ComputeBudgetProgram.setComputeUnitPrice({
    microLamports: 0
  }));
  tx.feePayer = payer;
  return tx;
}

function extractComputeUnitLimit(tx) {
  if ('version' in tx) {
    for (let ix of tx.message.compiledInstructions) {
      let programId = tx.message.staticAccountKeys[ix.programIdIndex];
      if (programId && ComputeBudgetProgram.programId.equals(programId)) {
        if (ix.data[0] === 2) {
          const decodedTI = new TransactionInstruction({
            keys: [],
            programId: ComputeBudgetProgram.programId,
            data: Buffer.from(ix.data)
          });
          return ComputeBudgetInstruction.decodeSetComputeUnitLimit(decodedTI).units;
        }
      }
    }
  } else {
    for (let ix of tx.instructions) {
      if (ComputeBudgetProgram.programId.equals(ix.programId)) {
        if (ix.data[0] === 2) {
          return ComputeBudgetInstruction.decodeSetComputeUnitLimit(ix).units;
        }
      }
    }
  }
  return null;
}
function extractComputeUnitPrice(tx) {
  if ('version' in tx) {
    for (let ix of tx.message.compiledInstructions) {
      let programId = tx.message.staticAccountKeys[ix.programIdIndex];
      if (programId && ComputeBudgetProgram.programId.equals(programId)) {
        if (ix.data[0] === 3) {
          const decodedTI = new TransactionInstruction({
            keys: [],
            programId: ComputeBudgetProgram.programId,
            data: Buffer.from(ix.data)
          });
          return ComputeBudgetInstruction.decodeSetComputeUnitPrice(decodedTI).microLamports;
        }
      }
    }
  } else {
    for (let ix of tx.instructions) {
      if (ComputeBudgetProgram.programId.equals(ix.programId)) {
        if (ix.data[0] === 3) {
          return ComputeBudgetInstruction.decodeSetComputeUnitPrice(ix).microLamports;
        }
      }
    }
  }
  return null;
}
const modifyComputeUnitLimitIx = (tx, newComputeUnitLimit) => {
  if ('version' in tx) {
    for (let ix of tx.message.compiledInstructions) {
      let programId = tx.message.staticAccountKeys[ix.programIdIndex];
      if (programId && ComputeBudgetProgram.programId.equals(programId)) {
        // need check for data index
        if (ix.data[0] === 2) {
          ix.data = Uint8Array.from(ComputeBudgetProgram.setComputeUnitLimit({
            units: newComputeUnitLimit
          }).data);
          return true;
        }
      }
    }
    // could not inject for VT
  } else {
    for (let ix of tx.instructions) {
      if (ComputeBudgetProgram.programId.equals(ix.programId)) {
        // need check for data index
        if (ix.data[0] === 2) {
          ix.data = ComputeBudgetProgram.setComputeUnitLimit({
            units: newComputeUnitLimit
          }).data;
          return true;
        }
      }
    }
    // inject if none
    tx.add(ComputeBudgetProgram.setComputeUnitLimit({
      units: newComputeUnitLimit
    }));
    return true;
  }
  return false;
};
/**
 * Modify priority fee in transaction
 * @param tx
 * @param newPriorityFee
 * @returns {boolean} true if priority fee was modified
 **/
const modifyComputeUnitPriceIx = (tx, newPriorityFee) => {
  if ('version' in tx) {
    for (let ix of tx.message.compiledInstructions) {
      let programId = tx.message.staticAccountKeys[ix.programIdIndex];
      if (programId && ComputeBudgetProgram.programId.equals(programId)) {
        // need check for data index
        if (ix.data[0] === 3) {
          ix.data = Uint8Array.from(ComputeBudgetProgram.setComputeUnitPrice({
            microLamports: newPriorityFee
          }).data);
          return true;
        }
      }
    }
    // could not inject for VT
  } else {
    for (let ix of tx.instructions) {
      if (ComputeBudgetProgram.programId.equals(ix.programId)) {
        // need check for data index
        if (ix.data[0] === 3) {
          ix.data = ComputeBudgetProgram.setComputeUnitPrice({
            microLamports: newPriorityFee
          }).data;
          return true;
        }
      }
    }
    // inject if none
    tx.add(ComputeBudgetProgram.setComputeUnitPrice({
      microLamports: newPriorityFee
    }));
    return true;
  }
  return false;
};
/**
 * Estimate and modify priority fee in transaction
 * @param connection
 * @param tx
 * @param newPriorityFee
 * @returns {number} estimated priority fee
 *  **/
const estimatePriorityFee = async (connection, params) => {
  let writeableAccounts = [];
  if (params.tx) {
    if ('version' in params.tx) {
      const accounts = params.tx.message.staticAccountKeys;
      for (const [idx, account] of accounts.entries()) {
        if (params.tx.message.isAccountWritable(idx)) {
          writeableAccounts.push(account);
        }
      }
    } else {
      for (const instruction of params.tx.instructions) {
        for (const account of instruction.keys) {
          if (account.isWritable) {
            writeableAccounts.push(account.pubkey);
          }
        }
      }
    }
  }
  if (params.accounts) {
    writeableAccounts = writeableAccounts.concat(params.accounts);
  }
  if (writeableAccounts.length) {
    let fees = await connection.getRecentPrioritizationFees({
      lockedWritableAccounts: writeableAccounts
    });
    fees = fees.sort((feeA, feeB) => feeB.slot - feeA.slot);
    fees = fees.slice(0, 20);
    let last20SortedFees = fees.map(fee => fee.prioritizationFee).sort((feeA, feeB) => feeB - feeA);
    let priorityFee = Math.max(last20SortedFees[Math.floor(last20SortedFees.length * 85 / 100)], 1); // minimum 1 microLamport
    return priorityFee;
  }
  return 1; // default 1
};

const checkTxSize = transaction => {
  let txLength = (() => {
    try {
      return transaction.serialize({
        requireAllSignatures: false
      }).length;
    } catch (e) {
      return PACKET_DATA_SIZE + 1;
    }
  })();
  if (txLength > PACKET_DATA_SIZE) {
    return {
      error: new TransactionError(COMMON_TX_ERRORS.TRANSACTION_TOO_LARGE)
    };
  }
};

const deserializeAccount = data => {
  if (data == undefined || data.length == 0) {
    return undefined;
  }
  const accountInfo = AccountLayout.decode(data);
  accountInfo.mint = new PublicKey(accountInfo.mint);
  accountInfo.owner = new PublicKey(accountInfo.owner);
  accountInfo.amount = u64.fromBuffer(accountInfo.amount);
  if (accountInfo.delegateOption === 0) {
    accountInfo.delegate = null;
    accountInfo.delegatedAmount = new u64(0);
  } else {
    accountInfo.delegate = new PublicKey(accountInfo.delegate);
    accountInfo.delegatedAmount = u64.fromBuffer(accountInfo.delegatedAmount);
  }
  accountInfo.isInitialized = accountInfo.state !== 0;
  accountInfo.isFrozen = accountInfo.state === 2;
  if (accountInfo.isNativeOption === 1) {
    accountInfo.rentExemptReserve = u64.fromBuffer(accountInfo.isNative);
    accountInfo.isNative = true;
  } else {
    accountInfo.rentExemptReserve = null;
    accountInfo.isNative = false;
  }
  if (accountInfo.closeAuthorityOption === 0) {
    accountInfo.closeAuthority = null;
  } else {
    accountInfo.closeAuthority = new PublicKey(accountInfo.closeAuthority);
  }
  return accountInfo;
};

const deserializeMint = (data, address) => {
  if (data.length !== MintLayout.span) {
    // This is causing problem with mainnet token
    // throw new Error('Not a valid Mint')
    return;
  }
  const mintInfo = MintLayout.decode(data);
  mintInfo.address = address;
  if (mintInfo.mintAuthorityOption === 0) {
    mintInfo.mintAuthority = null;
  } else {
    mintInfo.mintAuthority = new PublicKey(mintInfo.mintAuthority);
  }
  mintInfo.supply = u64.fromBuffer(mintInfo.supply);
  mintInfo.isInitialized = mintInfo.isInitialized !== 0;
  if (mintInfo.freezeAuthorityOption === 0) {
    mintInfo.freezeAuthority = null;
  } else {
    mintInfo.freezeAuthority = new PublicKey(mintInfo.freezeAuthority);
  }
  return mintInfo;
};

async function getWSolATA(connection, publicKey) {
  const wSolAddress = await Token.getAssociatedTokenAddress(ASSOCIATED_TOKEN_PROGRAM_ID, TOKEN_PROGRAM_ID, NATIVE_MINT, publicKey, true);
  const accountInfo = await connection.getAccountInfo(wSolAddress);
  return {
    address: wSolAddress,
    accountInfo
  };
}
async function createAndTransferWSOLTransaction({
  connection,
  publicKey,
  blockhash,
  lastValidBlockHeight,
  amountToTransferInLamports
}) {
  const wSolAccount = await getWSolATA(connection, publicKey);
  const instructions = [];
  // If no accountInfo, create one
  if (!wSolAccount.accountInfo) {
    // Create ATA account
    instructions.push(Token.createAssociatedTokenAccountInstruction(ASSOCIATED_TOKEN_PROGRAM_ID, TOKEN_PROGRAM_ID, NATIVE_MINT, wSolAccount.address, publicKey, publicKey));
  }
  // Fund account and sync
  instructions.push(SystemProgram.transfer({
    fromPubkey: publicKey,
    toPubkey: wSolAccount.address,
    lamports: amountToTransferInLamports
  }));
  instructions.push(
  // This is not exposed by the types, but indeed it exists
  Token.createSyncNativeInstruction(TOKEN_PROGRAM_ID, wSolAccount.address));
  const transaction = new Transaction({
    feePayer: publicKey,
    blockhash,
    lastValidBlockHeight
  });
  transaction.add(...instructions);
  return transaction;
}
async function createUnwrapSolTransaction({
  connection,
  publicKey,
  blockhash,
  lastValidBlockHeight
}) {
  const wSolAccount = await getWSolATA(connection, publicKey);
  const instructions = [];
  if (!wSolAccount.accountInfo) {
    return;
  }
  // Close account
  instructions.push(Token.createCloseAccountInstruction(TOKEN_PROGRAM_ID, wSolAccount.address, publicKey, publicKey, []));
  const transaction = new Transaction({
    feePayer: publicKey,
    blockhash,
    lastValidBlockHeight
  });
  transaction.add(...instructions);
  return transaction;
}

const getATA = async (tokenMint, owner, connection) => {
  let toAccount;
  try {
    toAccount = await getAssociatedTokenAddress(tokenMint, owner, true);
    const account = await connection.getAccountInfo(toAccount);
    return {
      account,
      toAccount
    };
  } catch (e) {
    /* handle error */
    console.error('Error::getATA', e);
    throw e;
  }
};
const getOrCreateATAInstruction = async (tokenMint, owner, connection, payer = owner) => {
  try {
    const {
      account,
      toAccount
    } = await getATA(tokenMint, owner, connection);
    if (!account) {
      const ix = createAssociatedTokenAccountIdempotentInstruction(payer, toAccount, owner, tokenMint);
      return [toAccount, ix];
    }
    return [toAccount, undefined];
  } catch (e) {
    /* handle error */
    console.error('Error::getOrCreateATAInstruction', e);
    throw e;
  }
};

const TOKEN_METADATA_PROGRAM_ID = /*#__PURE__*/new PublicKey('metaqbxxUerdq28cj1RbAWkYQm3ybzjb6a8bt518x1s');
// This is not an official program but a community deployement
const TOKEN_2022_METADATA_PROGRAM_ID = /*#__PURE__*/new PublicKey('META4s4fSmpkTbZoUsgC1oBnWB31vQcmnN8giPw51Zu');
function findMetadataAddress(mint, tokenProgramId) {
  const metadataProgramId = tokenProgramId.equals(TOKEN_PROGRAM_ID) ? TOKEN_METADATA_PROGRAM_ID : TOKEN_2022_METADATA_PROGRAM_ID;
  return PublicKey.findProgramAddressSync([Buffer.from('metadata'), metadataProgramId.toBuffer(), mint.toBuffer()], metadataProgramId)[0];
}
const TOKEN_2022_PROGRAM_ID = /*#__PURE__*/new PublicKey('TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb');
async function fetchMintInfos(connection, mints) {
  const results = (await utils.rpc.getMultipleAccounts(connection, mints)).reduce((acc, m, idx) => {
    if (m) {
      const {
        publicKey,
        account
      } = m;
      const isFromATokenProgram = account.owner.equals(TOKEN_PROGRAM_ID) || account.owner.equals(TOKEN_2022_PROGRAM_ID);
      if (isFromATokenProgram) {
        try {
          const mint = unpackMint(publicKey, account, account.owner);
          acc.push([mints[idx].toString(), {
            mintAuthority: mint.mintAuthority,
            supply: new u64(mint.supply.toString()),
            decimals: mint.decimals,
            isInitialized: mint.isInitialized,
            freezeAuthority: mint.freezeAuthority,
            programId: m.account.owner
          }]);
        } catch (error) {
          console.log('Failed to fetch or decode mint', mints[idx].toString(), error);
        }
      }
    }
    return acc;
  }, new Array());
  return results;
}
const removeEmptyChars = value => value.replace(/\u0000/g, '');
async function fetchTokenMetadatas(connection, tokens) {
  const metadataAddresses = tokens.map(t => findMetadataAddress(t.mint, t.programId));
  const tokenMetaAccounts = (await utils.rpc.getMultipleAccounts(connection, metadataAddresses)).reduce((acc, keyedAccount) => {
    if (keyedAccount) {
      const metadata = Metadata.deserialize(keyedAccount.account.data)[0];
      metadata.data.name = removeEmptyChars(metadata.data.name);
      metadata.data.symbol = removeEmptyChars(metadata.data.symbol);
      metadata.data.uri = removeEmptyChars(metadata.data.uri);
      acc.push({
        publicKey: keyedAccount.publicKey,
        account: metadata
      });
    }
    return acc;
  }, new Array());
  return tokenMetaAccounts;
}

function chunks(array, size) {
  return Array.apply(0, new Array(Math.ceil(array.length / size))).map((_, index) => array.slice(index * size, (index + 1) * size));
}

const getMultipleAccountsInfo = async (connection, keys, options) => {
  const accountToAccountInfoMap = new Map();
  await Promise.all(chunks(keys, 99).map(async chunk => {
    const accountInfos = await connection.getMultipleAccountsInfo(chunk, options);
    accountInfos.forEach((accountInfo, idx) => {
      accountToAccountInfoMap.set(chunk[idx].toString(), accountInfo);
    });
    return;
  }));
  return accountToAccountInfoMap;
};

export { COMMON_TX_ERRORS, TOKEN_2022_PROGRAM_ID, TransactionError, checkTxSize, createAndTransferWSOLTransaction, createJitoTipTx, createUnwrapSolTransaction, deserializeAccount, deserializeMint, estimatePriorityFee, extractComputeUnitLimit, extractComputeUnitPrice, fetchMintInfos, fetchTokenMetadatas, findMetadataAddress, forwardToJito, getATA, getMultipleAccountsInfo, getOrCreateATAInstruction, getRandomTipAccount, getSignature, handleSendTransaction, modifyComputeUnitLimitIx, modifyComputeUnitPriceIx, parseErrorForTransaction, parseErrorWithIdl, removeEmptyChars, transactionSenderAndConfirmationWaiter };
//# sourceMappingURL=optimist.esm.js.map
